import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import type { OrgProfile } from "@/components/org-settings/types";
import type { PublicCalendarEvent, PublicLeader, PublicMeetingSchedule, UnitType } from "@/types/database";

// Unit identity (number/council/district/chartered-org + type) as a public
// site should render it. Sourced from the shared unit_details record via the
// gateway RPC, not org_profiles — see
// scoutingops-supabase/.../20260720120000_public_site_unit_identity_gateway.sql.
export interface PublicUnitIdentity {
  unit_type: UnitType | null;
  unit_number: string | null;
  council_name: string | null;
  district_name: string | null;
  chartered_org_name: string | null;
}

// org_profiles is site-builder's own table and already has a public read
// policy scoped to orgs with a published site (public_read_published_site_owner,
// added in 20260713210941_consolidate_on_clerk_org_identity.sql) — this
// doesn't need the gateway RPCs below, which exist specifically for data
// owned by other repos.
export function usePublicOrgProfile(orgId: string | null) {
  return useQuery({
    queryKey: ["public-site-org-profile", orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<OrgProfile | null> => {
      const { data, error } = await supabase.from("org_profiles" as any).select("*").eq("clerk_org_id", orgId).maybeSingle();
      if (error) throw error;
      return data as unknown as OrgProfile | null;
    },
  });
}

// All three read through the public site data gateway (see
// supabase/migrations/20260715000000_public_site_data_gateway.sql) — each
// RPC is SECURITY DEFINER and gates entirely on the target org's own
// published state (plus, for events, calendar_visible_on_site), not on
// the caller's identity. That means the plain anon Supabase client works
// here whether this is called from the real public site or from Studio's
// authenticated canvas preview — deliberately the same client either way,
// since these functions were designed be identity-agnostic.

export function usePublicUnitIdentity(orgId: string | null) {
  return useQuery({
    queryKey: ["public-site-unit-identity", orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<PublicUnitIdentity | null> => {
      const { data, error } = await supabase.rpc("get_public_site_unit_identity" as any, { target_org_id: orgId });
      if (error) throw error;
      // SETOF function: zero rows when the site isn't published or has no
      // unit_details record yet.
      const rows = (data ?? []) as unknown as PublicUnitIdentity[];
      return rows[0] ?? null;
    },
  });
}

export function useLeadershipDirectory(orgId: string | null) {
  return useQuery({
    queryKey: ["public-site-leadership", orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<PublicLeader[]> => {
      const { data, error } = await supabase.rpc("get_public_site_leadership" as any, { target_org_id: orgId });
      if (error) throw error;
      return (data ?? []) as unknown as PublicLeader[];
    },
  });
}

export function useMeetingSchedule(orgId: string | null) {
  return useQuery({
    queryKey: ["public-site-meeting-schedule", orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<PublicMeetingSchedule[]> => {
      const { data, error } = await supabase.rpc("get_public_site_meeting_schedule" as any, { target_org_id: orgId });
      if (error) throw error;
      return (data ?? []) as unknown as PublicMeetingSchedule[];
    },
  });
}

export function useUpcomingEvents(orgId: string | null) {
  return useQuery({
    queryKey: ["public-site-calendar-events", orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<PublicCalendarEvent[]> => {
      const { data, error } = await supabase.rpc("get_public_site_calendar_events" as any, { target_org_id: orgId });
      if (error) throw error;
      return (data ?? []) as unknown as PublicCalendarEvent[];
    },
  });
}
